import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {Descriptor, MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {HalImage} from "@cm/hal"
import {Definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-patches/definition"
import {Vector2, Vector2Like} from "@cm/math"
import {getHalAddressMode} from "@cm/webgl2/utils"
import {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {
    AnyImageOpHalData,
    CtorArgs,
    ImageOpHalData,
    ImageOpHalDataParameters,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof Definition

export class ImageOpExtractPatchesHal extends ImageOpHal<Definition, ImageOpExtractPatchesHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpExtractPatchesHalData>): ImageOpExtractPatchesHalData {
        return new ImageOpExtractPatchesHalData({
            ...args,
            descriptor: this.determineDescriptor(args.parameters),
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
        })
    }

    protected determineDescriptor(parameters: ImageOpHalDataParameters<Definition>): Descriptor {
        const numPatches = this.computeNumPatches(parameters)
        return {
            width: numPatches.x * parameters.patchSize.width,
            height: numPatches.y * parameters.patchSize.height,
            channels: parameters.sourceImage.descriptor.channels,
            batching: {
                numPatches,
                patchSize: parameters.patchSize,
            },
        }
    }

    private computeNumPatches(parameters: ImageOpHalDataParameters<Definition>): Vector2Like {
        if (parameters.stride.x < 1 || parameters.stride.y < 1) {
            throw new Error("Stride must be >= 1")
        }
        const sourceRegion = parameters.sourceRegion ?? {
            x: 0,
            y: 0,
            width: parameters.sourceImage.descriptor.width,
            height: parameters.sourceImage.descriptor.height,
        }
        return {
            x: Math.floor((sourceRegion.width - parameters.patchSize.width) / parameters.stride.x) + 1,
            y: Math.floor((sourceRegion.height - parameters.patchSize.height) / parameters.stride.y) + 1,
        }
    }
}

class ImageOpExtractPatchesHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpExtractPatchesHalData>) {
        super(args)
        this.painter =
            args.prevData?.painter.addRef() ??
            this.ctx.createPainter(
                "compositor",
                `
            uniform int u_addressMode;
            uniform int u_useOffsetImage;
            uniform ivec2 u_offset;
            uniform ivec2 u_patchSize;
            uniform ivec2 u_stride;
        
            vec4 computeColor(ivec2 targetPixel) {
                ivec2 patchIndex = targetPixel / u_patchSize;
                ivec2 patchPixel = targetPixel % u_patchSize;
                ivec2 patchOffset = patchIndex * u_stride;
                ivec2 offset = u_offset;
                if (u_useOffsetImage != 0) {
                    offset += ivec2(texelFetch1(ivec2(0, 0)).xy);
                }
                ivec2 sourcePixel = offset + patchOffset + patchPixel;
                return texelFetch0(sourcePixel, u_addressMode);
            }
            `,
            )
    }

    protected override dispose() {
        this.painter.release()
        super.dispose()
    }

    // ImageHal
    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {sourceImage, sourceRegion: sourceRegionIn, patchSize, addressMode} = this.parameters
        sourceImage.addRef("ExtractPatches.writeRegion - sourceImage")
        const sourceRegion = sourceRegionIn ?? {
            x: 0,
            y: 0,
            width: sourceImage.descriptor.width,
            height: sourceImage.descriptor.height,
        }
        const offsetImage = "offsetImage" in sourceRegion ? sourceRegion.offsetImage : undefined
        offsetImage?.addRef("ExtractPatches.writeRegion - offsetImage")
        let sourceHalImage: RegionLockedHalImageRef | undefined
        let updatedOffsetImage: RegionLockedHalImageRef | undefined
        try {
            if (mipLevelRegion.mipLevel !== 0) {
                throw new Error("ExtractPatchesHal for mipLevel > 0 is not implemented !!!")
            }

            // if we have a dynamic source region (offsetImage), we update the entire source image
            const sourceMipLevelRegion: MipLevelRegion =
                "offsetImage" in sourceRegion || !sourceRegion
                    ? {
                          x: 0,
                          y: 0,
                          width: sourceImage.descriptor.width,
                          height: sourceImage.descriptor.height,
                          mipLevel: mipLevelRegion.mipLevel,
                      }
                    : {
                          ...sourceRegion,
                          mipLevel: mipLevelRegion.mipLevel,
                      }
            sourceHalImage = await sourceImage.getUpdatedHalImage(sourceMipLevelRegion, options)
            const offset = "offsetImage" in sourceRegion ? (sourceRegion.offset ?? {x: 0, y: 0}) : {x: sourceRegion.x, y: sourceRegion.y}
            updatedOffsetImage = await offsetImage?.getUpdatedHalImage({
                x: 0,
                y: 0,
                width: 1,
                height: 1,
                mipLevel: 0,
            })
            this.painter.ref.paint({
                target: outputImage,
                sourceImages: [sourceHalImage.halImage, updatedOffsetImage?.halImage],
                parameters: {
                    u_addressMode: {type: "int", value: getHalAddressMode(addressMode)},
                    u_useOffsetImage: {type: "int", value: offsetImage ? 1 : 0},
                    u_offset: {type: "int2", value: offset},
                    u_patchSize: {type: "int2", value: Vector2.fromSize2Like(patchSize)},
                    u_stride: {type: "int2", value: this.parameters.stride},
                },
            })
        } finally {
            sourceHalImage?.release()
            updatedOffsetImage?.release()
            sourceImage.release("ExtractPatches.writeRegion - sourceImage")
            offsetImage?.release("ExtractPatches.writeRegion - offsetImage")
        }
    }

    protected override onSourceRegionsInvalidated(sourceImage: AnyImageOpHalData, sourceRegions: Regions) {
        if (
            sourceRegions.length !== 1 ||
            sourceRegions[0].x !== 0 ||
            sourceRegions[0].y !== 0 ||
            sourceRegions[0].width !== sourceImage.descriptor.width ||
            sourceRegions[0].height !== sourceImage.descriptor.height
        ) {
            ImageOpExtractPatchesHalData.logger?.warn(
                "ExtractPatchesHal: onSourceRegionsInvalidated for partial source regions are not properly handled yet ! Invalidating entire output image.",
            )
        }
        this.invalidateRegions()
    }

    private painter: HalPainterImageCompositorRef
}
